import React, { useEffect, useState } from 'react';
import { EmailComposeMode, EmailReplyDraft, ReplyEmailResult } from '../types';

interface EmailComposeComposerProps {
  mode: EmailComposeMode;
  draft: EmailReplyDraft | null;
  loadingDraft: boolean;
  sending: boolean;
  error: string | null;
  onSend: (payload: { to: string; cc?: string; subject: string; text: string }) => Promise<ReplyEmailResult>;
  onCancel: () => void;
}

const MODE_LABELS: Record<EmailComposeMode, { title: string; send: string }> = {
  reply: { title: 'Reply', send: 'Send Reply' },
  'reply-all': { title: 'Reply All', send: 'Send Reply All' },
  forward: { title: 'Forward', send: 'Send Forward' }
};

const EmailComposeComposer: React.FC<EmailComposeComposerProps> = ({
  mode,
  draft,
  loadingDraft,
  sending,
  error,
  onSend,
  onCancel
}) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    if (!draft) return;
    setTo(draft.to);
    setCc(draft.cc || '');
    setSubject(draft.subject);
    setBody(draft.body);
    setSendError(null);
  }, [draft]);

  const handleSend = async () => {
    setSendError(null);
    try {
      await onSend({
        to: to.trim(),
        cc: cc.trim() || undefined,
        subject: subject.trim(),
        text: body
      });
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send message');
    }
  };

  const labels = MODE_LABELS[mode];
  const displayError = sendError || error;

  return (
    <div className="email-reply-composer">
      <div className="email-reply-composer-header">
        <h4>{labels.title}</h4>
        <button type="button" className="email-reply-cancel-btn" onClick={onCancel} disabled={sending}>
          Cancel
        </button>
      </div>

      {loadingDraft ? (
        <div className="email-reply-composer-loading">Preparing message…</div>
      ) : (
        <>
          <label className="email-reply-field">
            <span className="email-reply-field-label">To</span>
            <input
              type="text"
              className="app-form-input"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={sending}
              placeholder="recipient@example.com"
            />
          </label>

          {(mode === 'reply-all' || cc) && (
            <label className="email-reply-field">
              <span className="email-reply-field-label">Cc</span>
              <input
                type="text"
                className="app-form-input"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                disabled={sending}
              />
            </label>
          )}

          <label className="email-reply-field">
            <span className="email-reply-field-label">Subject</span>
            <input
              type="text"
              className="app-form-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
            />
          </label>

          <label className="email-reply-field email-reply-field--body">
            <span className="email-reply-field-label">Message</span>
            <textarea
              className="app-form-input email-reply-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              disabled={sending}
            />
          </label>

          {displayError && <div className="email-reply-error">{displayError}</div>}

          <div className="email-reply-actions">
            <button
              type="button"
              className="app-btn app-btn-primary"
              onClick={handleSend}
              disabled={sending || !to.trim() || !body.trim()}
            >
              {sending ? 'Sending…' : labels.send}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default EmailComposeComposer;
